/**
 * Web binding for the platform-agnostic auth core (M7.1; FR-2.11, roadmap 43).
 *
 * The identity + in-app registered-auth LOGIC lives in `@jeopardy/client-core`
 * (`auth-core.ts`), extracted for #104 so the native client reuses it verbatim. This
 * module wires that core to the browser: it injects the web's `SERVER_HTTP_URL`
 * base (D-104.12a), a `localStorage`-backed storage port (D-104.12b), and the
 * `localStorage`-backed anonymous funnel key. It creates ONE shared core instance so the
 * concurrent-mint dedup (#60) and single Cognito memory store behave exactly as before.
 *
 * Two layers, unchanged in behavior from the pre-#104 web module:
 *
 *   1. **Guest identity** (FR-2.1/2.3): a persistent `playerId` + a server-signed guest
 *      token, from `POST /api/auth/guest`, persisted in `localStorage`. Proves seat
 *      ownership on join/reconnect — the server resolves identity from the SIGNED TOKEN,
 *      never a raw client id (FR-9.3).
 *   2. **Registered upgrade** (FR-2.2, FR-2.11): app-owned, in-app SRP via
 *      `amazon-cognito-identity-js` (no Hosted-UI redirect). On sign-in the Cognito ID
 *      token is posted to `POST /api/account/claim`; Cognito tokens stay in in-memory
 *      storage only (D-43.6, NFR-5.5) — the durable session is the signed guest token.
 *
 * The re-exported functions ARE the client-core core's methods; only the browser-only
 * return-path routing + birth-year-prompt helpers (which drive web navigation, not the
 * portable auth core) are defined locally here against `localStorage`.
 */

import {
  createAuthCore,
  type Identity,
  type KeyValueStorage,
} from '@jeopardy/client-core';
import { SERVER_HTTP_URL } from '../config';
import { getFunnelKey } from './funnel-key';

/**
 * `localStorage`-backed storage port for the client-core auth core (D-104.12b). SSR-safe:
 * the build-time prerender (#103, FR-15.2) renders the landing — and its header's
 * SignIn → useAuth → `loadIdentity` — under Node, where there is no `localStorage`. Reads
 * return null (→ "no persisted identity", the browser reloads it on mount) and writes are
 * no-ops there, matching the original module's SSR guard.
 */
const webStorage: KeyValueStorage = {
  getItem: (key) =>
    typeof localStorage === 'undefined' ? null : localStorage.getItem(key),
  setItem: (key, value) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  },
  removeItem: (key) => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
  },
};

/**
 * The single shared auth core for the web app. One instance so the concurrent-mint
 * dedup (#60) and the Cognito in-memory store are shared across all callers (the core
 * owns that state per-instance — creating multiples would let first-use mints diverge).
 */
const core = createAuthCore({
  baseUrl: SERVER_HTTP_URL,
  storage: webStorage,
  getFunnelKey,
});

// Re-export the core's identity type and the pure validators unchanged.
export type { Identity } from '@jeopardy/client-core';
export {
  isPlausibleBirthYear,
  isValidDisplayName,
  DISPLAY_NAME_MAX_LEN,
} from '@jeopardy/client-core';

// The identity/auth surface consumers already import from `./auth`, now backed by the
// shared client-core core (behavior-preserving).
export const loadIdentity = core.loadIdentity;
export const saveIdentity = core.saveIdentity;
export const savePreferredName = core.savePreferredName;
export const saveAvatarUrl = core.saveAvatarUrl;
export const saveHasBirthYear = core.saveHasBirthYear;
export const ensureGuestIdentity = core.ensureGuestIdentity;
export const recoverStaleIdentity = core.recoverStaleIdentity;
export const register = core.register;
export const signIn = core.signIn;
export const forgotPassword = core.forgotPassword;
export const confirmReset = core.confirmReset;
export const claimAccount = core.claimAccount;
export const updateAvatar = core.updateAvatar;
export const clearAvatar = core.clearAvatar;
export const updateBirthYear = core.updateBirthYear;
export const updatePreferredName = core.updatePreferredName;
export const acceptTerms = core.acceptTerms;

// ---------------------------------------------------------------------------
// Browser-only state: birth-year prompt dismissal + post-sign-in return path
// (FR-2.8). These drive WEB navigation/prompt state, not the portable auth core, so
// they stay web-side against `localStorage` directly (the native client reimplements
// its own navigation).
// ---------------------------------------------------------------------------

/** Set once the user dismisses the "set your birth year" prompt (FR-2.8). */
const BIRTH_YEAR_PROMPT_DISMISSED_KEY = 'jeopardy.birthYearPromptDismissed';
/** Where to return after a sign-in round trip / the profile step (survives redirect). */
const RETURN_TO_KEY = 'jeopardy.returnTo';

/**
 * Sign out locally (drop the persisted registered identity → back to guest). Wraps the
 * core's identity clear to ALSO drop the web-only birth-year-prompt-dismissed flag, which
 * is per-registered-account and lives web-side ({@link BIRTH_YEAR_PROMPT_DISMISSED_KEY}) —
 * so a later fresh account without a birth year still gets prompted, matching the
 * pre-#104 behavior.
 */
export function clearIdentity(): void {
  core.clearIdentity();
  localStorage.removeItem(BIRTH_YEAR_PROMPT_DISMISSED_KEY);
}

/** True once the user has dismissed the "set your birth year" prompt (FR-2.8). */
export function isBirthYearPromptDismissed(): boolean {
  return localStorage.getItem(BIRTH_YEAR_PROMPT_DISMISSED_KEY) === '1';
}
/** Remember that the user dismissed the birth-year prompt (so we don't re-nag). */
export function dismissBirthYearPrompt(): void {
  localStorage.setItem(BIRTH_YEAR_PROMPT_DISMISSED_KEY, '1');
}

/** Stash where to return after the sign-in round trip / profile step (survives redirect). */
export function stashReturnTo(path: string): void {
  localStorage.setItem(RETURN_TO_KEY, path);
}
/** Consume the stashed return path (one-shot), or null if none. */
export function takeReturnTo(): string | null {
  const v = localStorage.getItem(RETURN_TO_KEY);
  if (v) localStorage.removeItem(RETURN_TO_KEY);
  return v;
}

/**
 * Where to land after a successful sign-in + claim (FR-2.8). A newly registered user
 * with no birth year is sent to `/profile` to set one — returnTo is left stashed for
 * ProfilePage to resume after Save/Skip. Everyone else resumes their stashed returnTo
 * (consumed here), defaulting home. Same routing the M7.2 Hosted-UI callback used.
 */
export function resolvePostSignInTarget(identity: Identity): string {
  if (!identity.isGuest && !identity.hasBirthYear) return '/profile';
  return takeReturnTo() ?? '/';
}
